// Copyright Square, Inc.

// Generated using eventGen — https://github.com/tidal-engineering/experimentation-cdf
// Do not edit directly!

/*
 */
export type BrowseContentType =
  | 'album'
  | 'artist'
  | 'live'
  | 'mix'
  | 'playlist'
  | 'track'
  | 'user'
  | 'video';

/**
 * @see BrowsePreviewDJSessionEvent
 */
export type BrowsePreviewDJSessionParams = {
  /**
   * Current route.
   */
  currentRoute: string;
  /**
   * DJ User id.
   */
  djId: number;
  /**
   * Is session live?
   */
  isLive: boolean;
  /**
   * Internal or external page from where the user is coming from.
   */
  referrer: string;
};

/**
 * Browse Preview DJSession Event
 *
 * **Entity**: Browse - Refers to www.tidal.com/browse service.
 *
 * **Action**: Preview - When a user navigates through TIDAL browse pages, they can preview different types of content such as albums, artists, live sessions, mixes, playlists, and tracks.
 *
 * **Event**: DJSession - Triggers when a user lands on www.tidal.com/browse/live/[djUserId]
 */
export type BrowsePreviewDJSessionEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Browse_Preview_DJSession';
  parameters: {
    cdf_action: 'Preview';
    cdf_entity: 'Browse';
  } & BrowsePreviewDJSessionParams;
  version: 1;
};

/**
 * @see BrowsePreviewDJSessionEvent
 */
export const BrowsePreviewDJSession = ({
  currentRoute,
  djId,
  isLive,
  referrer,
}: BrowsePreviewDJSessionParams): BrowsePreviewDJSessionEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Browse_Preview_DJSession',
  parameters: {
    cdf_action: 'Preview',
    cdf_entity: 'Browse',
    currentRoute: currentRoute,
    djId: djId,
    isLive: isLive,
    referrer: referrer,
  },
  version: 1,
});

/**
 * @see BrowsePreviewTrackEvent
 */
export type BrowsePreviewTrackParams = {
  /**
   * UTM ad ID.
   */
  adId: string;
  /**
   * UTM ad position.
   */
  adPosition: string;
  /**
   * UTM adset ID.
   */
  adsetId: string;
  /**
   * UTM adset name.
   */
  adsetName: string;
  /**
   * The UTM campaign ID provides the details of the channel, campaign name, etc, allowing us to attribute the user visit to that original location.
   */
  campaignId: string;
  /**
   * UTM campaign type.
   */
  campaignType: string;
  /**
   * UTM placement. Page from where the user is coming from.
   */
  placement: string;
  /**
   * UTM search query passes the exact keyword string the user input into the search engine.
   */
  query: string;
  /**
   * Internal or external page from where the user is coming from.
   */
  referrer: string;
  /**
   * The unique campaign name for a promotion and or channel.
   */
  utmCampaign: string;
  /**
   * Used to differentiate similar content, or links within the same ad. For example, if you have two call-to-action links within the same email message, you can use utm_content and set different values for each so you can tell which version is more effective.
   */
  utmContent: string;
  /**
   * The advertising or marketing medium, for example: cpc, banner, email newsletter.
   */
  utmMedium: string;
  /**
   * Identify the advertiser, site, publication, etc. that is sending traffic to your property, for example Paid Search, Paid social, email, etc.
   */
  utmSource: string;
  /**
   * The unique campaign name for a promotion and or channel.
   */
  utmTerm: string;
};

/**
 * Browse Preview Track Event
 *
 * **Entity**: Browse - Refers to www.tidal.com/browse service.
 *
 * **Action**: Preview - When a user navigates through TIDAL browse pages, they can preview different types of content such as albums, artists, live sessions, mixes, playlists, and tracks.
 *
 * **Event**: Track - Triggers when a user lands on a track sharing page https://tidal.com/browse/track/[trackid]
 */
export type BrowsePreviewTrackEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Browse_Preview_Track';
  parameters: {
    cdf_action: 'Preview';
    cdf_entity: 'Browse';
  } & BrowsePreviewTrackParams;
  version: 1;
};

/**
 * @see BrowsePreviewTrackEvent
 */
export const BrowsePreviewTrack = ({
  adId,
  adPosition,
  adsetId,
  adsetName,
  campaignId,
  campaignType,
  placement,
  query,
  referrer,
  utmCampaign,
  utmContent,
  utmMedium,
  utmSource,
  utmTerm,
}: BrowsePreviewTrackParams): BrowsePreviewTrackEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Browse_Preview_Track',
  parameters: {
    adId: adId,
    adPosition: adPosition,
    adsetId: adsetId,
    adsetName: adsetName,
    campaignId: campaignId,
    campaignType: campaignType,
    cdf_action: 'Preview',
    cdf_entity: 'Browse',
    placement: placement,
    query: query,
    referrer: referrer,
    utmCampaign: utmCampaign,
    utmContent: utmContent,
    utmMedium: utmMedium,
    utmSource: utmSource,
    utmTerm: utmTerm,
  },
  version: 1,
});

/**
 * @see BrowsePreviewArtistEvent
 */
export type BrowsePreviewArtistParams = {
  /**
   * UTM ad ID.
   */
  adId: string;
  /**
   * UTM ad position.
   */
  adPosition: string;
  /**
   * UTM adset ID.
   */
  adsetId: string;
  /**
   * UTM adset name.
   */
  adsetName: string;
  /**
   * The UTM campaign ID provides the details of the channel, campaign name, etc, allowing us to attribute the user visit to that original location.
   */
  campaignId: string;
  /**
   * UTM campaign type.
   */
  campaignType: string;
  /**
   * UTM placement. Page from where the user is coming from.
   */
  placement: string;
  /**
   * UTM search query passes the exact keyword string the user input into the search engine.
   */
  query: string;
  /**
   * Internal or external page from where the user is coming from.
   */
  referrer: string;
  /**
   * The unique campaign name for a promotion and or channel.
   */
  utmCampaign: string;
  /**
   * Used to differentiate similar content, or links within the same ad. For example, if you have two call-to-action links within the same email message, you can use utm_content and set different values for each so you can tell which version is more effective.
   */
  utmContent: string;
  /**
   * The advertising or marketing medium, for example: cpc, banner, email newsletter.
   */
  utmMedium: string;
  /**
   * Identify the advertiser, site, publication, etc. that is sending traffic to your property, for example Paid Search, Paid social, email, etc.
   */
  utmSource: string;
  /**
   * The unique campaign name for a promotion and or channel.
   */
  utmTerm: string;
};

/**
 * Browse Preview Artist Event
 *
 * **Entity**: Browse - Refers to www.tidal.com/browse service.
 *
 * **Action**: Preview - When a user navigates through TIDAL browse pages, they can preview different types of content such as albums, artists, live sessions, mixes, playlists, and tracks.
 *
 * **Event**: Artist - Triggers when a user lands on a artist sharing page https://tidal.com/browse/artist/[artistId]
 */
export type BrowsePreviewArtistEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Browse_Preview_Artist';
  parameters: {
    cdf_action: 'Preview';
    cdf_entity: 'Browse';
  } & BrowsePreviewArtistParams;
  version: 1;
};

/**
 * @see BrowsePreviewArtistEvent
 */
export const BrowsePreviewArtist = ({
  adId,
  adPosition,
  adsetId,
  adsetName,
  campaignId,
  campaignType,
  placement,
  query,
  referrer,
  utmCampaign,
  utmContent,
  utmMedium,
  utmSource,
  utmTerm,
}: BrowsePreviewArtistParams): BrowsePreviewArtistEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Browse_Preview_Artist',
  parameters: {
    adId: adId,
    adPosition: adPosition,
    adsetId: adsetId,
    adsetName: adsetName,
    campaignId: campaignId,
    campaignType: campaignType,
    cdf_action: 'Preview',
    cdf_entity: 'Browse',
    placement: placement,
    query: query,
    referrer: referrer,
    utmCampaign: utmCampaign,
    utmContent: utmContent,
    utmMedium: utmMedium,
    utmSource: utmSource,
    utmTerm: utmTerm,
  },
  version: 1,
});

/**
 * @see BrowsePreviewPlaylistEvent
 */
export type BrowsePreviewPlaylistParams = {
  /**
   * UTM ad ID.
   */
  adId: string;
  /**
   * UTM ad position.
   */
  adPosition: string;
  /**
   * UTM adset ID.
   */
  adsetId: string;
  /**
   * UTM adset name.
   */
  adsetName: string;
  /**
   * The UTM campaign ID provides the details of the channel, campaign name, etc, allowing us to attribute the user visit to that original location.
   */
  campaignId: string;
  /**
   * UTM campaign type.
   */
  campaignType: string;
  /**
   * UTM placement. Page from where the user is coming from.
   */
  placement: string;
  /**
   * UTM search query passes the exact keyword string the user input into the search engine.
   */
  query: string;
  /**
   * Internal or external page from where the user is coming from.
   */
  referrer: string;
  /**
   * The unique campaign name for a promotion and or channel.
   */
  utmCampaign: string;
  /**
   * Used to differentiate similar content, or links within the same ad. For example, if you have two call-to-action links within the same email message, you can use utm_content and set different values for each so you can tell which version is more effective.
   */
  utmContent: string;
  /**
   * The advertising or marketing medium, for example: cpc, banner, email newsletter.
   */
  utmMedium: string;
  /**
   * Identify the advertiser, site, publication, etc. that is sending traffic to your property, for example Paid Search, Paid social, email, etc.
   */
  utmSource: string;
  /**
   * The unique campaign name for a promotion and or channel.
   */
  utmTerm: string;
};

/**
 * Browse Preview Playlist Event
 *
 * **Entity**: Browse - Refers to www.tidal.com/browse service.
 *
 * **Action**: Preview - When a user navigates through TIDAL browse pages, they can preview different types of content such as albums, artists, live sessions, mixes, playlists, and tracks.
 *
 * **Event**: Playlist - Triggers when a user lands on a playlist sharing page https://tidal.com/browse/playlist/[playlistId]
 */
export type BrowsePreviewPlaylistEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Browse_Preview_Playlist';
  parameters: {
    cdf_action: 'Preview';
    cdf_entity: 'Browse';
  } & BrowsePreviewPlaylistParams;
  version: 1;
};

/**
 * @see BrowsePreviewPlaylistEvent
 */
export const BrowsePreviewPlaylist = ({
  adId,
  adPosition,
  adsetId,
  adsetName,
  campaignId,
  campaignType,
  placement,
  query,
  referrer,
  utmCampaign,
  utmContent,
  utmMedium,
  utmSource,
  utmTerm,
}: BrowsePreviewPlaylistParams): BrowsePreviewPlaylistEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Browse_Preview_Playlist',
  parameters: {
    adId: adId,
    adPosition: adPosition,
    adsetId: adsetId,
    adsetName: adsetName,
    campaignId: campaignId,
    campaignType: campaignType,
    cdf_action: 'Preview',
    cdf_entity: 'Browse',
    placement: placement,
    query: query,
    referrer: referrer,
    utmCampaign: utmCampaign,
    utmContent: utmContent,
    utmMedium: utmMedium,
    utmSource: utmSource,
    utmTerm: utmTerm,
  },
  version: 1,
});

/**
 * @see BrowsePreviewMixEvent
 */
export type BrowsePreviewMixParams = {
  /**
   * UTM ad ID.
   */
  adId: string;
  /**
   * UTM ad position.
   */
  adPosition: string;
  /**
   * UTM adset ID.
   */
  adsetId: string;
  /**
   * UTM adset name.
   */
  adsetName: string;
  /**
   * The UTM campaign ID provides the details of the channel, campaign name, etc, allowing us to attribute the user visit to that original location.
   */
  campaignId: string;
  /**
   * UTM campaign type.
   */
  campaignType: string;
  /**
   * UTM placement. Page from where the user is coming from.
   */
  placement: string;
  /**
   * UTM search query passes the exact keyword string the user input into the search engine.
   */
  query: string;
  /**
   * Internal or external page from where the user is coming from.
   */
  referrer: string;
  /**
   * The unique campaign name for a promotion and or channel.
   */
  utmCampaign: string;
  /**
   * Used to differentiate similar content, or links within the same ad. For example, if you have two call-to-action links within the same email message, you can use utm_content and set different values for each so you can tell which version is more effective.
   */
  utmContent: string;
  /**
   * The advertising or marketing medium, for example: cpc, banner, email newsletter.
   */
  utmMedium: string;
  /**
   * Identify the advertiser, site, publication, etc. that is sending traffic to your property, for example Paid Search, Paid social, email, etc.
   */
  utmSource: string;
  /**
   * The unique campaign name for a promotion and or channel.
   */
  utmTerm: string;
};

/**
 * Browse Preview Mix Event
 *
 * **Entity**: Browse - Refers to www.tidal.com/browse service.
 *
 * **Action**: Preview - When a user navigates through TIDAL browse pages, they can preview different types of content such as albums, artists, live sessions, mixes, playlists, and tracks.
 *
 * **Event**: Mix - Triggers when a user lands on a mix sharing page https://tidal.com/browse/mix/[mixId]
 */
export type BrowsePreviewMixEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Browse_Preview_Mix';
  parameters: {
    cdf_action: 'Preview';
    cdf_entity: 'Browse';
  } & BrowsePreviewMixParams;
  version: 1;
};

/**
 * @see BrowsePreviewMixEvent
 */
export const BrowsePreviewMix = ({
  adId,
  adPosition,
  adsetId,
  adsetName,
  campaignId,
  campaignType,
  placement,
  query,
  referrer,
  utmCampaign,
  utmContent,
  utmMedium,
  utmSource,
  utmTerm,
}: BrowsePreviewMixParams): BrowsePreviewMixEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Browse_Preview_Mix',
  parameters: {
    adId: adId,
    adPosition: adPosition,
    adsetId: adsetId,
    adsetName: adsetName,
    campaignId: campaignId,
    campaignType: campaignType,
    cdf_action: 'Preview',
    cdf_entity: 'Browse',
    placement: placement,
    query: query,
    referrer: referrer,
    utmCampaign: utmCampaign,
    utmContent: utmContent,
    utmMedium: utmMedium,
    utmSource: utmSource,
    utmTerm: utmTerm,
  },
  version: 1,
});

/**
 * @see BrowsePreviewAlbumEvent
 */
export type BrowsePreviewAlbumParams = {
  /**
   * UTM ad ID.
   */
  adId: string;
  /**
   * UTM ad position.
   */
  adPosition: string;
  /**
   * UTM adset ID.
   */
  adsetId: string;
  /**
   * UTM adset name.
   */
  adsetName: string;
  /**
   * The UTM campaign ID provides the details of the channel, campaign name, etc, allowing us to attribute the user visit to that original location.
   */
  campaignId: string;
  /**
   * UTM campaign type.
   */
  campaignType: string;
  /**
   * UTM placement. Page from where the user is coming from.
   */
  placement: string;
  /**
   * UTM search query passes the exact keyword string the user input into the search engine.
   */
  query: string;
  /**
   * Internal or external page from where the user is coming from.
   */
  referrer: string;
  /**
   * The unique campaign name for a promotion and or channel.
   */
  utmCampaign: string;
  /**
   * Used to differentiate similar content, or links within the same ad. For example, if you have two call-to-action links within the same email message, you can use utm_content and set different values for each so you can tell which version is more effective.
   */
  utmContent: string;
  /**
   * The advertising or marketing medium, for example: cpc, banner, email newsletter.
   */
  utmMedium: string;
  /**
   * Identify the advertiser, site, publication, etc. that is sending traffic to your property, for example Paid Search, Paid social, email, etc.
   */
  utmSource: string;
  /**
   * The unique campaign name for a promotion and or channel.
   */
  utmTerm: string;
};

/**
 * Browse Preview Album Event
 *
 * **Entity**: Browse - Refers to www.tidal.com/browse service.
 *
 * **Action**: Preview - When a user navigates through TIDAL browse pages, they can preview different types of content such as albums, artists, live sessions, mixes, playlists, and tracks.
 *
 * **Event**: Album - Triggers when a user lands on a album sharing page https://tidal.com/browse/album/[albumId]
 */
export type BrowsePreviewAlbumEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Browse_Preview_Album';
  parameters: {
    cdf_action: 'Preview';
    cdf_entity: 'Browse';
  } & BrowsePreviewAlbumParams;
  version: 1;
};

/**
 * @see BrowsePreviewAlbumEvent
 */
export const BrowsePreviewAlbum = ({
  adId,
  adPosition,
  adsetId,
  adsetName,
  campaignId,
  campaignType,
  placement,
  query,
  referrer,
  utmCampaign,
  utmContent,
  utmMedium,
  utmSource,
  utmTerm,
}: BrowsePreviewAlbumParams): BrowsePreviewAlbumEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Browse_Preview_Album',
  parameters: {
    adId: adId,
    adPosition: adPosition,
    adsetId: adsetId,
    adsetName: adsetName,
    campaignId: campaignId,
    campaignType: campaignType,
    cdf_action: 'Preview',
    cdf_entity: 'Browse',
    placement: placement,
    query: query,
    referrer: referrer,
    utmCampaign: utmCampaign,
    utmContent: utmContent,
    utmMedium: utmMedium,
    utmSource: utmSource,
    utmTerm: utmTerm,
  },
  version: 1,
});

/**
 * @see BrowsePreviewUserProfileEvent
 */
export type BrowsePreviewUserProfileParams = {
  /**
   * UTM ad ID.
   */
  adId: string;
  /**
   * UTM ad position.
   */
  adPosition: string;
  /**
   * UTM adset ID.
   */
  adsetId: string;
  /**
   * UTM adset name.
   */
  adsetName: string;
  /**
   * The UTM campaign ID provides the details of the channel, campaign name, etc, allowing us to attribute the user visit to that original location.
   */
  campaignId: string;
  /**
   * UTM campaign type.
   */
  campaignType: string;
  /**
   * UTM placement. Page from where the user is coming from.
   */
  placement: string;
  /**
   * UTM search query passes the exact keyword string the user input into the search engine.
   */
  query: string;
  /**
   * Internal or external page from where the user is coming from.
   */
  referrer: string;
  /**
   * The unique campaign name for a promotion and or channel.
   */
  utmCampaign: string;
  /**
   * Used to differentiate similar content, or links within the same ad. For example, if you have two call-to-action links within the same email message, you can use utm_content and set different values for each so you can tell which version is more effective.
   */
  utmContent: string;
  /**
   * The advertising or marketing medium, for example: cpc, banner, email newsletter.
   */
  utmMedium: string;
  /**
   * Identify the advertiser, site, publication, etc. that is sending traffic to your property, for example Paid Search, Paid social, email, etc.
   */
  utmSource: string;
  /**
   * The unique campaign name for a promotion and or channel.
   */
  utmTerm: string;
};

/**
 * Browse Preview UserProfile Event
 *
 * **Entity**: Browse - Refers to www.tidal.com/browse service.
 *
 * **Action**: Preview - When a user navigates through TIDAL browse pages, they can preview different types of content such as albums, artists, live sessions, mixes, playlists, and tracks.
 *
 * **Event**: UserProfile - Triggers when a user lands on a user profile sharing page https://tidal.com/browse/user/[userId]
 */
export type BrowsePreviewUserProfileEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Browse_Preview_UserProfile';
  parameters: {
    cdf_action: 'Preview';
    cdf_entity: 'Browse';
  } & BrowsePreviewUserProfileParams;
  version: 1;
};

/**
 * @see BrowsePreviewUserProfileEvent
 */
export const BrowsePreviewUserProfile = ({
  adId,
  adPosition,
  adsetId,
  adsetName,
  campaignId,
  campaignType,
  placement,
  query,
  referrer,
  utmCampaign,
  utmContent,
  utmMedium,
  utmSource,
  utmTerm,
}: BrowsePreviewUserProfileParams): BrowsePreviewUserProfileEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Browse_Preview_UserProfile',
  parameters: {
    adId: adId,
    adPosition: adPosition,
    adsetId: adsetId,
    adsetName: adsetName,
    campaignId: campaignId,
    campaignType: campaignType,
    cdf_action: 'Preview',
    cdf_entity: 'Browse',
    placement: placement,
    query: query,
    referrer: referrer,
    utmCampaign: utmCampaign,
    utmContent: utmContent,
    utmMedium: utmMedium,
    utmSource: utmSource,
    utmTerm: utmTerm,
  },
  version: 1,
});

/**
 * @see BrowsePreviewVideoEvent
 */
export type BrowsePreviewVideoParams = {
  /**
   * UTM ad ID.
   */
  adId: string;
  /**
   * UTM ad position.
   */
  adPosition: string;
  /**
   * UTM adset ID.
   */
  adsetId: string;
  /**
   * UTM adset name.
   */
  adsetName: string;
  /**
   * The UTM campaign ID provides the details of the channel, campaign name, etc, allowing us to attribute the user visit to that original location.
   */
  campaignId: string;
  /**
   * UTM campaign type.
   */
  campaignType: string;
  /**
   * UTM placement. Page from where the user is coming from.
   */
  placement: string;
  /**
   * UTM search query passes the exact keyword string the user input into the search engine.
   */
  query: string;
  /**
   * Internal or external page from where the user is coming from.
   */
  referrer: string;
  /**
   * The unique campaign name for a promotion and or channel.
   */
  utmCampaign: string;
  /**
   * Used to differentiate similar content, or links within the same ad. For example, if you have two call-to-action links within the same email message, you can use utm_content and set different values for each so you can tell which version is more effective.
   */
  utmContent: string;
  /**
   * The advertising or marketing medium, for example: cpc, banner, email newsletter.
   */
  utmMedium: string;
  /**
   * Identify the advertiser, site, publication, etc. that is sending traffic to your property, for example Paid Search, Paid social, email, etc.
   */
  utmSource: string;
  /**
   * The unique campaign name for a promotion and or channel.
   */
  utmTerm: string;
};

/**
 * Browse Preview Video Event
 *
 * **Entity**: Browse - Refers to www.tidal.com/browse service.
 *
 * **Action**: Preview - When a user navigates through TIDAL browse pages, they can preview different types of content such as albums, artists, live sessions, mixes, playlists, and tracks.
 *
 * **Event**: Video - Triggers when a user lands on a video sharing page https://tidal.com/browse/video/[videoId]
 */
export type BrowsePreviewVideoEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Browse_Preview_Video';
  parameters: {
    cdf_action: 'Preview';
    cdf_entity: 'Browse';
  } & BrowsePreviewVideoParams;
  version: 1;
};

/**
 * @see BrowsePreviewVideoEvent
 */
export const BrowsePreviewVideo = ({
  adId,
  adPosition,
  adsetId,
  adsetName,
  campaignId,
  campaignType,
  placement,
  query,
  referrer,
  utmCampaign,
  utmContent,
  utmMedium,
  utmSource,
  utmTerm,
}: BrowsePreviewVideoParams): BrowsePreviewVideoEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Browse_Preview_Video',
  parameters: {
    adId: adId,
    adPosition: adPosition,
    adsetId: adsetId,
    adsetName: adsetName,
    campaignId: campaignId,
    campaignType: campaignType,
    cdf_action: 'Preview',
    cdf_entity: 'Browse',
    placement: placement,
    query: query,
    referrer: referrer,
    utmCampaign: utmCampaign,
    utmContent: utmContent,
    utmMedium: utmMedium,
    utmSource: utmSource,
    utmTerm: utmTerm,
  },
  version: 1,
});

/**
 * @see BrowseClickGetStartedEvent
 */
export type BrowseClickGetStartedParams = {
  /**
   * Which button the user clicked on.
   */
  buttonVariant: BrowseClickGetStartedButtonVariant;
  /**
   * Specify which type of page the user is currently on
   */
  contentType: BrowseContentType;
};

/**
 * Browse Click GetStarted Event
 *
 * **Entity**: Browse - Refers to www.tidal.com/browse service.
 *
 * **Action**: Click - Triggers when a user clicks on a button, asset, or element that sends them to another internal or external URL.
 *
 * **Event**: GetStarted - Triggers when a user clicks on the 'Get Started' or 'Free Trial' button in the shared content page.
 */
export type BrowseClickGetStartedEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Browse_Click_GetStarted';
  parameters: {
    cdf_action: 'Click';
    cdf_entity: 'Browse';
  } & BrowseClickGetStartedParams;
  version: 1;
};

/**
 * @see BrowseClickGetStartedEvent
 */
export const BrowseClickGetStarted = ({
  buttonVariant,
  contentType,
}: BrowseClickGetStartedParams): BrowseClickGetStartedEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Browse_Click_GetStarted',
  parameters: {
    buttonVariant: buttonVariant,
    cdf_action: 'Click',
    cdf_entity: 'Browse',
    contentType: contentType,
  },
  version: 1,
});

/*
 */
export type BrowseClickGetStartedButtonVariant =
  | 'start_free_trial'
  | 'start_free_trial_banner'
  | 'start_free_trial_sticky';

/**
 * @see BrowseClickOpenAppEvent
 */
export type BrowseClickOpenAppParams = {
  /**
   * Which button the user clicked on.
   */
  buttonVariant: BrowseClickOpenAppButtonVariant;
  /**
   * Specify which type of page the user is currently on
   */
  contentType: BrowseContentType;
};

/**
 * Browse Click OpenApp Event
 *
 * **Entity**: Browse - Refers to www.tidal.com/browse service.
 *
 * **Action**: Click - Triggers when a user clicks on a button, asset, or element that sends them to another internal or external URL.
 *
 * **Event**: OpenApp - Triggers when a user clicks on the 'Open App' button in the shared content page.
 */
export type BrowseClickOpenAppEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Browse_Click_OpenApp';
  parameters: {
    cdf_action: 'Click';
    cdf_entity: 'Browse';
  } & BrowseClickOpenAppParams;
  version: 1;
};

/**
 * @see BrowseClickOpenAppEvent
 */
export const BrowseClickOpenApp = ({
  buttonVariant,
  contentType,
}: BrowseClickOpenAppParams): BrowseClickOpenAppEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Browse_Click_OpenApp',
  parameters: {
    buttonVariant: buttonVariant,
    cdf_action: 'Click',
    cdf_entity: 'Browse',
    contentType: contentType,
  },
  version: 1,
});

/*
 */
export type BrowseClickOpenAppButtonVariant = 'open_app' | 'play_tidal';

/**
 * @see BrowseClickMenuEvent
 */
export type BrowseClickMenuParams = {
  /**
   * Specify which type of page the user is currently on
   */
  contentType: BrowseContentType;
};

/**
 * Browse Click Menu Event
 *
 * **Entity**: Browse - Refers to www.tidal.com/browse service.
 *
 * **Action**: Click - Triggers when a user clicks on a button, asset, or element that sends them to another internal or external URL.
 *
 * **Event**: Menu - Triggers when a user clicks on the (burger) menu on mobile in the shared content page.
 */
export type BrowseClickMenuEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Browse_Click_Menu';
  parameters: {
    cdf_action: 'Click';
    cdf_entity: 'Browse';
  } & BrowseClickMenuParams;
  version: 1;
};

/**
 * @see BrowseClickMenuEvent
 */
export const BrowseClickMenu = ({
  contentType,
}: BrowseClickMenuParams): BrowseClickMenuEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Browse_Click_Menu',
  parameters: {
    cdf_action: 'Click',
    cdf_entity: 'Browse',
    contentType: contentType,
  },
  version: 1,
});

/**
 * Browse Onboarding Success Event
 *
 * **Entity**: Browse - Refers to www.tidal.com/browse service.
 *
 * **Action**: Onboarding - User onboarding through shared content in tidal.com/browse.
 *
 * **Event**: Success - User onboarded successfully. Triggers when a user lands on offer.tidal.com/onboarding/success.
 */
export type BrowseOnboardingSuccessEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Browse_Onboarding_Success';
  parameters: {
    cdf_action: 'Onboarding';
    cdf_entity: 'Browse';
  };
  version: 1;
};

/**
 * @see BrowseOnboardingSuccessEvent
 */
export const BrowseOnboardingSuccess = (): BrowseOnboardingSuccessEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Browse_Onboarding_Success',
  parameters: {
    cdf_action: 'Onboarding',
    cdf_entity: 'Browse',
  },
  version: 1,
});

/**
 * Browse Onboarding DownloadApp Event
 *
 * **Entity**: Browse - Refers to www.tidal.com/browse service.
 *
 * **Action**: Onboarding - User onboarding through shared content in tidal.com/browse.
 *
 * **Event**: DownloadApp - Triggers when a user clicks download app on offer.tidal.com/onboarding/success.
 */
export type BrowseOnboardingDownloadAppEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Browse_Onboarding_DownloadApp';
  parameters: {
    cdf_action: 'Onboarding';
    cdf_entity: 'Browse';
  };
  version: 1;
};

/**
 * @see BrowseOnboardingDownloadAppEvent
 */
export const BrowseOnboardingDownloadApp =
  (): BrowseOnboardingDownloadAppEvent => ({
    consentCategory: 'PERFORMANCE',
    group: 'onboarding',
    name: 'Browse_Onboarding_DownloadApp',
    parameters: {
      cdf_action: 'Onboarding',
      cdf_entity: 'Browse',
    },
    version: 1,
  });

export type BrowseEvents =
  | BrowseClickGetStartedEvent
  | BrowseClickMenuEvent
  | BrowseClickOpenAppEvent
  | BrowseOnboardingDownloadAppEvent
  | BrowseOnboardingSuccessEvent
  | BrowsePreviewAlbumEvent
  | BrowsePreviewArtistEvent
  | BrowsePreviewDJSessionEvent
  | BrowsePreviewMixEvent
  | BrowsePreviewPlaylistEvent
  | BrowsePreviewTrackEvent
  | BrowsePreviewUserProfileEvent
  | BrowsePreviewVideoEvent;
